import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Wallet, TrendingUp, DollarSign, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import analyticsService from '../services/analyticsService';

const Portfolio = ({ userId = 'demo_user' }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPortfolio = async () => {
    setLoading(true);
    try {
      const data = await analyticsService.getPortfolio(userId);
      setPortfolio(data);
    } catch (error) {
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [userId]);

  if (loading && !portfolio) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-gray-500">Loading portfolio...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="portfolio-container">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold" data-testid="total-value">
              ${portfolio?.total_value_usd?.toFixed(2) || '0.00'}
            </p>
            <p className="text-xs text-gray-500 mt-1">USD</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              TON Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold" data-testid="ton-balance">
              {portfolio?.total_ton?.toFixed(4) || '0.0000'}
            </p>
            <p className="text-xs text-gray-500 mt-1">TON</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wallet className="h-4 w-4 text-purple-600" />
              Wallets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{portfolio?.wallets_count || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Connected</p>
          </CardContent>
        </Card>
      </div>

      {/* Token Holdings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Token Holdings</CardTitle>
              <CardDescription>Your token balances across all wallets</CardDescription>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchPortfolio}
              disabled={loading}
              data-testid="refresh-portfolio-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {portfolio?.tokens?.length > 0 ? (
            <div className="space-y-3">
              {portfolio.tokens.map((token, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`token-${token.symbol}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {token.symbol[0]}
                    </div>
                    <div>
                      <p className="font-medium">{token.symbol}</p>
                      <p className="text-sm text-gray-500">
                        {token.balance?.toFixed(4)} tokens
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">${token.value_usd?.toFixed(2)}</p>
                    <p className="text-sm text-gray-500">
                      ${token.price_usd?.toFixed(4)}/token
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No tokens found</p>
              <p className="text-sm mt-2">Start trading to build your portfolio</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Last Updated */}
      <p className="text-xs text-center text-gray-500">
        Last updated: {portfolio?.last_updated ? new Date(portfolio.last_updated).toLocaleString() : 'Never'}
      </p>
    </div>
  );
};

export default Portfolio;
